/**
 * Generic OAuth callback page — /oauth/callback  (PUBLIC, popup-only relay)
 *
 * Microsoft (and any future provider) redirects the OAuth popup here after consent.
 * This page is a THIN RELAY — it never exchanges the code and needs no app session:
 *   1. Read `code`+`state` (or `error`/`error_description`) from the query string.
 *   2. If opened as a popup, postMessage them back to `window.opener` (origin-locked)
 *      and auto-close the window.
 *   3. The opener tab (already authenticated) performs the `/oauth2/exchange`.
 *
 * Full-page fallback (no opener): we can't exchange without the channel context, so
 * we just tell the user to retry from the Teams channel page.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardBody, Spinner } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';

export default function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  const isPopup = typeof window !== 'undefined' && !!window.opener;
  const [relayed, setRelayed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!window.opener) return; // full-page fallback — nothing to relay.

    try {
      window.opener.postMessage(
        {
          type: 'oauth-callback',
          code: code ?? undefined,
          state: state ?? undefined,
          error: error ? errorDescription || error : undefined,
        },
        window.location.origin, // origin-locked target — never '*'.
      );
    } catch {
      // opener may be cross-origin or gone — safe to ignore.
    }
    setRelayed(true);
    // Brief pause so the user sees the confirmation before the window closes.
    const t = setTimeout(() => window.close(), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={screenStyle}>
      <Card style={{ width: '100%', maxWidth: 420 }}>
        <CardBody>
          {isPopup ? (
            <div style={bodyStyle}>
              {error ? (
                <XCircle size={56} color="var(--vn-danger-solid)" />
              ) : (
                <CheckCircle2 size={56} color="var(--vn-success-solid)" />
              )}
              <p style={titleStyle}>{error ? 'Authorization failed' : 'Almost done'}</p>
              <p style={hintStyle}>
                {error
                  ? 'Returning to the app — this window will close automatically.'
                  : 'You can close this window. Returning to the app…'}
              </p>
              {!relayed && <Spinner />}
            </div>
          ) : (
            <div style={bodyStyle}>
              <XCircle size={56} color="var(--vn-warning-solid)" />
              <p style={titleStyle}>Nothing to do here</p>
              <Alert tone="warning">
                Please go back to the app and start the connection again from your Teams channel.
              </Alert>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

const screenStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 'var(--vn-space-5)',
  background: 'var(--vn-bg-canvas)',
};

const bodyStyle: React.CSSProperties = {
  display: 'grid',
  justifyItems: 'center',
  gap: 'var(--vn-space-4)',
  textAlign: 'center',
  padding: 'var(--vn-space-4) 0',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--vn-font-size-lg)',
  fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'],
  color: 'var(--vn-fg-default)',
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--vn-font-size-sm)',
  color: 'var(--vn-fg-muted)',
};
